<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcBillingList',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    companyId: {
      type: String,
      required: false,
    },
    clientId: {
      type: String,
      required: false,
    },
    rentalId: {
      type: String,
      required: false,
    },
    contractId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return {
      company_id: props.companyId,
      client_id: props.clientId,
      rental_id: props.rentalId,
      contract_id: props.contractId,
    };
  };
  const onCreateInvoicesPayload = (...args) => {
    return {
      items: selectedItems.value.map((p) => ({ id: p.id })),
    };

    return $imodal.value
      .show({ name: 'CompleteInvoicesAlt' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return selectedItems.value.map((p) => ({
          ...result.value,
          invoice_id: p.id,
        }));
      });
  };
  const onCreateBillingPlan = (...args) => {
    return {
      items: selectedItems.value.map((p) => ({ id: p.id })),
    };

    return $imodal.value.show({ name: 'CreateBillingPlan' }).then((result) => {
      if (!result || result.cancel || !result.value) {
        return;
      }
      return selectedItems.value.map((p) => ({
        ...result.value,
        invoice_id: p.id,
      }));
    });
  };
  const getDownloadViewsPayload = (...args) => {
    return {
      identities: _.chain(selectedItems.value)
        .filter((p) => p.invoice && p.invoice.id)
        .map((p) => p.invoice.id)
        .uniq()
        .value(),
    };
  };

  const gridName = ref('billingList');
  const messages = ref({
    title: $fn.value.pgettext('billingList|Title', 'Billing list'),
    exportFilename: $fn.value.pgettext(
      'billingList|Export|Filename',
      'billing-list'
    ),
  });
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_7315092382398562_columns = computed(() => [
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('BillingList', 'Billing due date'),
      type: 'date',
    },
    {
      field: 'invoice',
      name: $fn.value.pgettext('BillingList', 'Invoice'),
      params: {
        sortPath: 'invoice.invoice_number',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('BillingList', 'Payment due date'),
      type: 'date',
    },
    {
      name: $fn.value.pgettext('BillingList', 'Period start'),
      params: {
        sortPath: 'period.start',
      },
      field: 'period.start',
      type: 'date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'period.start',
          },
        },
      ],
    },
    {
      name: $fn.value.pgettext('BillingList', 'Period end'),
      params: {
        sortPath: 'period.end',
      },
      field: 'period.end',
      type: 'date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'period.end',
          },
        },
      ],
    },
    {
      field: 'company',
      name: $fn.value.pgettext('BillingList', 'Company'),
      params: {
        sortPath: 'company.code',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('BillingList', 'Partner'),
      params: {
        sortPath: 'partner.code',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'partner.id',
            },
          },
        ],
      },
    },
    {
      field: 'contract',
      name: $fn.value.pgettext('BillingList', 'Contract'),
      params: {
        sortPath: 'contract.contract_number',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.contract_number }}">{{ $value.contract_number }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract.id',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('BillingList', 'Rental'),
      params: {
        sortPath: 'rental.name',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental.id',
            },
          },
        ],
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('BillingList', 'Description'),
      type: 'command',
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('BillingList', 'Billing category'),
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('BillingList', 'Notes'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('BillingList', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('BillingList', 'Unit'),
    },
    {
      field: 'unit_price',
      name: $fn.value.pgettext('BillingList', 'Price'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('BillingList', 'Currency'),
    },
    {
      field: 'discount',
      name: $fn.value.pgettext('BillingList', 'Discount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('BillingList', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'tax_code',
      name: $fn.value.pgettext('BillingList', 'Tax code'),
    },
    {
      field: 'exchange_rate',
      name: $fn.value.pgettext('BillingList', 'Exchrate'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <span title="{{ $value.value }}">{{ $value.value }}</span>\n\
\n\
    {{~#if $value.date }}\n\
      <span>&middot; {{ $value.date }}</span>\n\
    {{~/if }}\n\
\n\
    {{~#if $value.source }}\n\
      <span>&middot; {{ $value.source }}</span>\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('BillingList', 'Intermediated Service'),
      formatter: 'checkmark',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    messages: messages.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_columnDefaults: b_7315092382398562_columnDefaults.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_billing_list_item', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'messages' },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_columnDefaults', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_columns', nodeId: '7315092382398562' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      messages,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_columnDefaults,
      b_7315092382398562_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.clientId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clientId');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );

  const act_7315092382398562_setData_b1volm09t_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7315092382398562_script_f55rpddr0_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_f55rpddr0_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_f55rpddr0_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_oqpp9uozw_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingListItem',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_kxp29ky8u_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'description';
  };
  const act_7315092382398562_script_b2jzg73lz_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_b2jzg73lz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_b2jzg73lz_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_40mi1m82g_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.invoice.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_97gknv4uu_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice' && !!$event.data.data.invoice;
  };
  const act_7315092382398562_script_8fonph6pp_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_8fonph6pp_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_8fonph6pp_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_ef9eafeq8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Company',
      props: {
        oid: $event.data.data.company.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_g58kupsns_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'company' && !!$event.data.data.company;
  };
  const act_7315092382398562_script_8c3cb40t6_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_8c3cb40t6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_8c3cb40t6_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_g8brrhn99_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Company',
      props: {
        oid: $event.data.data.partner.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_egte3fhgd_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'partner' && !!$event.data.data.partner;
  };
  const act_2095089031469157_reloadSlickgrid_w01714dqg_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_2095089031469157_close_nz4kdmujj_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_2liep2xs3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateBillingPlan',
    };
  };
  const act_9004123373732307_reloadSlickgrid_pbkcv3477_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_9004123373732307_close_20n77e9af_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_9004123373732307_showModal_q2fsc10o3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingListItem',
    };
  };
  const act_397195341670858_reloadSlickgrid_12zlfvghn_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_397195341670858_close_dbfwswf9x_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_397195341670858_showModal_eh9mkly10_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CopyBillingListItems',
      props: {
        sources: _.map(selectedItems.value, 'id'),
      },
    };
  };
  const act_8823104369878821_reloadSlickgrid_wzl0yks2r_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingList',
    };
  };
  const act_8823104369878821_request_dplluwkyb_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_many_billing_list_item',
      data: { items: selectedItems.value.map((p) => p.id) },
    };
  };
  const act_7454831470842007_reloadSlickgrid_5s4km28es_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_7454831470842007_close_0ta12buox_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_7454831470842007_showModal_v285hkr42_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BatchBillingListEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_2237382557075493_reloadSlickgrid_7nfui33ue_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_2237382557075493_close_h12o7h0jr_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2237382557075493_showModal_54q4n2rd0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadBillingListItems',
    };
  };
  const act_8320016629450276_reloadSlickgrid_obad7vpoy_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6705964765214521_reloadSlickgrid_m4wmusstf_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6705964765214521_request_46p9gyh5i_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'merge_invoices',
      data: {
        items: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'invoice.id') != null)
          .map((p) => p.invoice.id)
          .uniq()
          .value(),
      },
    };
  };
  const act_6806515264385235_reloadSlickgrid_h4s865s9i_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6806515264385235_request_8n5kl9xr6_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'invoice.id') != null)
          .map((p) => p.invoice.id)
          .uniq()
          .value(),
      },
    };
  };

  const act_7315092382398562_setData_b1volm09t = new actionTypes.SetDataAction({
    actionArgs: act_7315092382398562_setData_b1volm09t_getActionArgs,
  });
  const inst_evh_7315092382398562_selectedRowsChanged_27l3wv5t8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_setData_b1volm09t,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_7315092382398562_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_selectedRowsChanged_27l3wv5t8],
      event: 'selectedRowsChanged',
    });
  const act_7315092382398562_script_f55rpddr0 = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_f55rpddr0_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_vmbf2ymq3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_f55rpddr0,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_oqpp9uozw =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_oqpp9uozw_getActionArgs,
      events: [inst_evh_7315092382398562_saved_vmbf2ymq3],
    });
  const inst_evh_7315092382398562_command_kxp29ky8u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_oqpp9uozw,
      event: 'command',
      when: inst_evh_7315092382398562_command_kxp29ky8u_getWhen,
      displayName: 'showModal',
    });
  const act_7315092382398562_script_b2jzg73lz = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_b2jzg73lz_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_17115vdvf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_b2jzg73lz,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_40mi1m82g =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_40mi1m82g_getActionArgs,
      events: [inst_evh_7315092382398562_saved_17115vdvf],
    });
  const inst_evh_7315092382398562_command_97gknv4uu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_40mi1m82g,
      event: 'command',
      when: inst_evh_7315092382398562_command_97gknv4uu_getWhen,
      displayName: 'showModal',
    });
  const act_7315092382398562_script_8fonph6pp = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_8fonph6pp_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_dcbgcpieh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_8fonph6pp,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_ef9eafeq8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_ef9eafeq8_getActionArgs,
      events: [inst_evh_7315092382398562_saved_dcbgcpieh],
    });
  const inst_evh_7315092382398562_command_g58kupsns =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_ef9eafeq8,
      event: 'command',
      when: inst_evh_7315092382398562_command_g58kupsns_getWhen,
      displayName: 'showModal',
    });
  const act_7315092382398562_script_8c3cb40t6 = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_8c3cb40t6_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_1wv2vmabm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_8c3cb40t6,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_g8brrhn99 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_g8brrhn99_getActionArgs,
      events: [inst_evh_7315092382398562_saved_1wv2vmabm],
    });
  const inst_evh_7315092382398562_command_egte3fhgd =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_g8brrhn99,
      event: 'command',
      when: inst_evh_7315092382398562_command_egte3fhgd_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_7315092382398562_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7315092382398562_command_kxp29ky8u,
      inst_evh_7315092382398562_command_97gknv4uu,
      inst_evh_7315092382398562_command_g58kupsns,
      inst_evh_7315092382398562_command_egte3fhgd,
    ],
    event: 'command',
  });
  const act_2095089031469157_reloadSlickgrid_w01714dqg =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2095089031469157_reloadSlickgrid_w01714dqg_getActionArgs,
    });
  const inst_evh_2095089031469157_close_nz4kdmujj =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadSlickgrid_w01714dqg,
      event: 'close',
      when: inst_evh_2095089031469157_close_nz4kdmujj_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2095089031469157_showModal_2liep2xs3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_2liep2xs3_getActionArgs,
      events: [inst_evh_2095089031469157_close_nz4kdmujj],
    });
  const inst_evh_2095089031469157_click_51eb64qms =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_2liep2xs3,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_51eb64qms],
    event: 'click',
  });
  const act_9004123373732307_reloadSlickgrid_pbkcv3477 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_9004123373732307_reloadSlickgrid_pbkcv3477_getActionArgs,
    });
  const inst_evh_9004123373732307_close_20n77e9af =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_reloadSlickgrid_pbkcv3477,
      event: 'close',
      when: inst_evh_9004123373732307_close_20n77e9af_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_9004123373732307_showModal_q2fsc10o3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_9004123373732307_showModal_q2fsc10o3_getActionArgs,
      events: [inst_evh_9004123373732307_close_20n77e9af],
    });
  const inst_evh_9004123373732307_click_ond4nzpux =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_showModal_q2fsc10o3,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_9004123373732307_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_9004123373732307_click_ond4nzpux],
    event: 'click',
  });
  const act_397195341670858_reloadSlickgrid_12zlfvghn =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_397195341670858_reloadSlickgrid_12zlfvghn_getActionArgs,
    });
  const inst_evh_397195341670858_close_dbfwswf9x =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_reloadSlickgrid_12zlfvghn,
      event: 'close',
      when: inst_evh_397195341670858_close_dbfwswf9x_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_397195341670858_showModal_eh9mkly10 =
    new actionTypes.ShowModalAction({
      actionArgs: act_397195341670858_showModal_eh9mkly10_getActionArgs,
      events: [inst_evh_397195341670858_close_dbfwswf9x],
    });
  const inst_evh_397195341670858_click_sejlrtkjt =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_showModal_eh9mkly10,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_397195341670858_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_397195341670858_click_sejlrtkjt],
    event: 'click',
  });
  const act_8823104369878821_reloadSlickgrid_wzl0yks2r =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_8823104369878821_reloadSlickgrid_wzl0yks2r_getActionArgs,
    });
  const inst_evh_8823104369878821_success_nm0aaytif =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_reloadSlickgrid_wzl0yks2r,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_8823104369878821_request_dplluwkyb = new actionTypes.RequestAction({
    actionArgs: act_8823104369878821_request_dplluwkyb_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_8823104369878821_success_nm0aaytif],
  });
  const inst_evh_8823104369878821_click_uibsyicmg =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_request_dplluwkyb,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_8823104369878821_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8823104369878821_click_uibsyicmg],
    event: 'click',
  });
  const act_7454831470842007_reloadSlickgrid_5s4km28es =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7454831470842007_reloadSlickgrid_5s4km28es_getActionArgs,
    });
  const inst_evh_7454831470842007_close_0ta12buox =
    new actionTypes.EventHandlerImpl({
      action: act_7454831470842007_reloadSlickgrid_5s4km28es,
      event: 'close',
      when: inst_evh_7454831470842007_close_0ta12buox_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_7454831470842007_showModal_v285hkr42 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7454831470842007_showModal_v285hkr42_getActionArgs,
      events: [inst_evh_7454831470842007_close_0ta12buox],
    });
  const inst_evh_7454831470842007_click_k4fb2by9v =
    new actionTypes.EventHandlerImpl({
      action: act_7454831470842007_showModal_v285hkr42,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7454831470842007_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7454831470842007_click_k4fb2by9v],
    event: 'click',
  });
  const act_2237382557075493_reloadSlickgrid_7nfui33ue =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2237382557075493_reloadSlickgrid_7nfui33ue_getActionArgs,
    });
  const inst_evh_2237382557075493_close_h12o7h0jr =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_reloadSlickgrid_7nfui33ue,
      event: 'close',
      when: inst_evh_2237382557075493_close_h12o7h0jr_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_2237382557075493_showModal_54q4n2rd0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2237382557075493_showModal_54q4n2rd0_getActionArgs,
      events: [inst_evh_2237382557075493_close_h12o7h0jr],
    });
  const inst_evh_2237382557075493_click_2fy6t1f3b =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_showModal_54q4n2rd0,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2237382557075493_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2237382557075493_click_2fy6t1f3b],
    event: 'click',
  });
  const act_8320016629450276_reloadSlickgrid_obad7vpoy =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_8320016629450276_reloadSlickgrid_obad7vpoy_getActionArgs,
    });
  const inst_evh_8320016629450276_success_gquamuiie =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadSlickgrid_obad7vpoy,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_gquamuiie],
    event: 'success',
  });
  const act_6705964765214521_reloadSlickgrid_m4wmusstf =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6705964765214521_reloadSlickgrid_m4wmusstf_getActionArgs,
    });
  const inst_evh_6705964765214521_success_h390z4uok =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_reloadSlickgrid_m4wmusstf,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_6705964765214521_request_46p9gyh5i = new actionTypes.RequestAction({
    actionArgs: act_6705964765214521_request_46p9gyh5i_getActionArgs,
    displayName: 'mergeInvoices',
    events: [inst_evh_6705964765214521_success_h390z4uok],
  });
  const inst_evh_6705964765214521_click_de1obp0wq =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_request_46p9gyh5i,
      event: 'click',
      displayName: 'mergeInvoices',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_de1obp0wq],
    event: 'click',
  });
  const act_6806515264385235_reloadSlickgrid_h4s865s9i =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6806515264385235_reloadSlickgrid_h4s865s9i_getActionArgs,
    });
  const inst_evh_6806515264385235_success_9o9f2qpo2 =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_reloadSlickgrid_h4s865s9i,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_6806515264385235_request_8n5kl9xr6 = new actionTypes.RequestAction({
    actionArgs: act_6806515264385235_request_8n5kl9xr6_getActionArgs,
    displayName: 'deleteInvoices',
    events: [inst_evh_6806515264385235_success_9o9f2qpo2],
  });
  const inst_evh_6806515264385235_click_glqpg8lsz =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_request_8n5kl9xr6,
      event: 'click',
      displayName: 'deleteInvoices',
    });
  const inst_evh_6806515264385235_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6806515264385235_click_glqpg8lsz],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_selectedRowsChanged_27l3wv5t8,
    inst_evh_7315092382398562_selectedRowsChanged,
    inst_evh_7315092382398562_saved_vmbf2ymq3,
    inst_evh_7315092382398562_command_kxp29ky8u,
    inst_evh_7315092382398562_saved_17115vdvf,
    inst_evh_7315092382398562_command_97gknv4uu,
    inst_evh_7315092382398562_saved_dcbgcpieh,
    inst_evh_7315092382398562_command_g58kupsns,
    inst_evh_7315092382398562_saved_1wv2vmabm,
    inst_evh_7315092382398562_command_egte3fhgd,
    inst_evh_7315092382398562_command,
    inst_evh_2095089031469157_close_nz4kdmujj,
    inst_evh_2095089031469157_click_51eb64qms,
    inst_evh_2095089031469157_click,
    inst_evh_9004123373732307_close_20n77e9af,
    inst_evh_9004123373732307_click_ond4nzpux,
    inst_evh_9004123373732307_click,
    inst_evh_397195341670858_close_dbfwswf9x,
    inst_evh_397195341670858_click_sejlrtkjt,
    inst_evh_397195341670858_click,
    inst_evh_8823104369878821_success_nm0aaytif,
    inst_evh_8823104369878821_click_uibsyicmg,
    deleteItems: inst_evh_8823104369878821_click_uibsyicmg,
    inst_evh_8823104369878821_click,
    inst_evh_7454831470842007_close_0ta12buox,
    inst_evh_7454831470842007_click_k4fb2by9v,
    inst_evh_7454831470842007_click,
    inst_evh_2237382557075493_close_h12o7h0jr,
    inst_evh_2237382557075493_click_2fy6t1f3b,
    inst_evh_2237382557075493_click,
    inst_evh_8320016629450276_success_gquamuiie,
    inst_evh_8320016629450276_success,
    inst_evh_6705964765214521_success_h390z4uok,
    inst_evh_6705964765214521_click_de1obp0wq,
    mergeInvoices: inst_evh_6705964765214521_click_de1obp0wq,
    inst_evh_6705964765214521_click,
    inst_evh_6806515264385235_success_9o9f2qpo2,
    inst_evh_6806515264385235_click_glqpg8lsz,
    deleteInvoices: inst_evh_6806515264385235_click_glqpg8lsz,
    inst_evh_6806515264385235_click,
  };

  const evh_7315092382398562_selectedRowsChanged_27l3wv5t8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged_27l3wv5t8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_vmbf2ymq3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_vmbf2ymq3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_kxp29ky8u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_kxp29ky8u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_17115vdvf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_17115vdvf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_97gknv4uu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_97gknv4uu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_dcbgcpieh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_dcbgcpieh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_g58kupsns = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_g58kupsns.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_1wv2vmabm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_1wv2vmabm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_egte3fhgd = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_egte3fhgd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_nz4kdmujj = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_nz4kdmujj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_51eb64qms = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_51eb64qms.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_close_20n77e9af = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_close_20n77e9af.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click_ond4nzpux = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click_ond4nzpux.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_close_dbfwswf9x = async (event, scope) => {
    return await actions.inst_evh_397195341670858_close_dbfwswf9x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click_sejlrtkjt = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click_sejlrtkjt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_success_nm0aaytif = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_success_nm0aaytif.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click_uibsyicmg = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click_uibsyicmg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_close_0ta12buox = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_close_0ta12buox.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_click_k4fb2by9v = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_click_k4fb2by9v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_click = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_close_h12o7h0jr = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_close_h12o7h0jr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_click_2fy6t1f3b = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_click_2fy6t1f3b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_click = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_gquamuiie = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_gquamuiie.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success_h390z4uok = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success_h390z4uok.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click_de1obp0wq = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_de1obp0wq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_success_9o9f2qpo2 = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_success_9o9f2qpo2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_click_glqpg8lsz = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_click_glqpg8lsz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_click = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <VueSlickgrid
    name="billingList"
    :headerTitle="messages.title"
    :items="ds"
    checkbox-row-selection
    export-to-csv
    :exportToCsvFilename="messages.exportFilename"
    :columnDefaults="b_7315092382398562_columnDefaults"
    :columns="b_7315092382398562_columns"
    ref="grid"
    @selectedRowsChanged="evh_7315092382398562_selectedRowsChanged($event, {})"
    @command="evh_7315092382398562_command($event, {})"
  >
    <template #toolbar>
      <div class="flex-grow-1 d-flex gap-2 border-start ps-3">
        <BDropdown size="sm" toggle-class="btn-light border">
          <template #button-content>
            <ItpIcon :icon="$config.billingList.createItemIcon" class="mx-1">
            </ItpIcon>
            <ItpText :text="pgettext('BillingList', 'Billing plan')"> </ItpText>
          </template>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Create billing plan...')"
            size="sm"
            dropdown-item
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Add item...')"
            size="sm"
            dropdown-item
            @click="evh_9004123373732307_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Copy items...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            dropdown-item
            @click="evh_397195341670858_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            dropdown-item
            @click="evh_8823104369878821_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Bulk edit...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            dropdown-item
            @click="evh_7454831470842007_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.uploadIcon"
            :text="pgettext('BillingList', 'Bulk upload...')"
            size="sm"
            dropdown-item
            @click="evh_2237382557075493_click($event, {})"
          >
          </ItpNavItem>
        </BDropdown>
        <JobHandlerControl
          size="sm"
          jobType="invoice_generation_from_billing_list"
          load-current
          :payload="onCreateInvoicesPayload"
          icon="mdi:cog"
          :text="pgettext('BillingList|Submit', 'Generate invoices')"
          :disabled="!selectedItems || !selectedItems.length"
          @success="evh_8320016629450276_success($event, {})"
        >
        </JobHandlerControl>
        <ItpButton
          :icon="$config.mergeIcon"
          :text="gettext('Merge invoices')"
          size="sm"
          variant="light"
          :disabled="!selectedItems || !selectedItems.length"
          :spinning="actions.mergeInvoices.isRunning"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          icon="fa-trash"
          :text="gettext('Delete invoices')"
          size="sm"
          variant="light"
          :disabled="!selectedItems || !selectedItems.length"
          :spinning="actions.deleteInvoices.isRunning"
          @click="evh_6806515264385235_click($event, {})"
        >
        </ItpButton>
        <ArtifactJob
          icon="ic:baseline-download"
          :text="gettext('Download views')"
          size="sm"
          :disabled="!selectedItems || !selectedItems.length"
          operation="download_invoice_views"
          :payload="getDownloadViewsPayload"
        >
        </ArtifactJob>
      </div>
    </template>
  </VueSlickgrid>
</template>
